import { BottomNav, Footer, Header, NewNavbar, OfflineContent } from '@/components/organisms';
import Notification, { notify } from 'react-notify-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { ACTION_AUTH } from './redux/auth/auth.action';
import { ACTION_CLIENT } from './redux/client/client.action';
import { COLOR } from '@/config';
import { HELPERS } from './utils';
import Head from '@/components/layouts/head';
import { MobileMenuProvider } from '@/contexts/MobileMenuContext';
import PATH from './routes/path';
import ReactPixel from 'react-facebook-pixel';
import Routes from './routes';
import { APIShimmerBanner } from '@/components/atoms/StickyBanner/shimmerBanner';
import TopLoader from 'react-top-loader';
import { useBanner } from '@/hooks/useBanner';
import { useBannerVisibility } from '@/hooks/useBannerVisibility';
import usePageTracking from './hooks/usePageTracking';

const App = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [isOnline, setIsOnline] = useState(true);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { isBannerVisible, bannerHeight, navbarHeight } = useBannerVisibility();

  // Banner data
  const { data: banners, isLoading: isBannerLoading, error: bannerError } = useBanner();

  // Banner data available

  const { showTopLoadingBar, showHeader, showFooter, showBottomNav } = useSelector((state) => state.activity);
  const { isLoading: isClientLoading } = useSelector((state) => state.client);
  const isLoggedIn = useSelector((state) => state.auth.token);
  const auth = useSelector((state) => state.auth);
  const { shouldRedirectToLogin, authExpiryMessage } = useSelector((state) => state.auth);

  useEffect(() => {
    if (shouldRedirectToLogin) {
      const currentPath = history.location.pathname + history.location.search;
      history.push(PATH.AUTH_LOGIN, { returnTo: currentPath });
      dispatch(ACTION_AUTH.clearAuthRedirect());
    }
  }, [shouldRedirectToLogin, history, dispatch]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const unlisten = history.listen((x) => {
      window.scrollTo({ top: 0 });
    });

    const handleOnline = () => {
      setIsOnline(true);
      notify.show('Koneksi telah terhubung', 'success');
    };

    const handleOffline = () => {
      setIsOnline(false);
      notify.show('Tidak bisa terhubung ke server, silahkan cek koneksi Anda', 'warning');
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    const initializeApp = async () => {
      try {
        const clientDataPromise = dispatch(ACTION_CLIENT.getClientData());
        const socialMediaPromise = dispatch(ACTION_CLIENT.getSocialMedia());

        await Promise.all([clientDataPromise, socialMediaPromise]);

        setIsInitialLoadComplete(true);

        // Analytics
        HELPERS.GA.page();
        ReactPixel.init(
          process.env.REACT_APP_FACEBOOK_PIXEL,
          {},
          {
            autoConfig: false,
            debug: false,
          }
        );

        HELPERS.saveUTMParameters();
      } catch (error) {
        console.error('Failed to initialize app:', error);
        notify.show('Gagal memuat data awal. Silakan coba lagi.', 'error');
        setIsInitialLoadComplete(true);
      }
    };

    initializeApp();

    return () => {
      unlisten();
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [dispatch, history]);

  usePageTracking();

  useEffect(() => {
    if (isLoggedIn) {
      HELPERS.GA.identify(auth?.profile?.uuid, { email: auth?.profile?.email, name: auth?.profile?.name });
    }
  }, [isLoggedIn]);

  const newLandingPaths = [PATH.HOME, PATH.SUBSCRIPTION_PACKAGE];
  const isNewLandingPage = newLandingPaths.includes(location.pathname);

  // Determine when to show StickyBanner - only show on marketing-focused pages
  const shouldShowStickyBanner = () => {
    const currentPath = location.pathname;

    // Marketing-focused pages where banner should appear
    const marketingFocusedPaths = [
      PATH.HOME,
      PATH.LANDING_COURSE_PAGE,
      PATH.CATALOG,
      PATH.CATALOG_COURSE,
      PATH.CATALOG_EVENT,
      PATH.EVENT,
      PATH.ARTICLE,
      PATH.NEW_LANDING_PAGE,
      PATH.NOT_FOUND, // /404

      // Subscription pages
      PATH.SUBSCRIPTION_PACKAGE, // /subscription

      // Other utility pages
      PATH.FAQ, // /faq
      PATH.TERMS_AND_CONDITION, // /terms-and-condition
      PATH.PRIVACY_POLICY, // /privacy-policy
      PATH.LEARNING_PROGRAM, // /learning-program
      PATH.LEARNING_PROGRAM_DETAIL, // /learning-program/:slug
      PATH.LEARNING_PROGRAM_PACKAGE, // /learning-program/:slug/subscription-package
    ];

    // Pages where banner should NOT appear (forms, quiz, profile, etc.)
    const excludedPaths = [
      // Assessment/Quiz pages
      PATH.ASSESSMENT, // /personal-learning-program/quiz
      PATH.ASSESSMENT_JOURNEY, PATH.ASSESSMENT_HISTORY, // /personal-learning-program
      PATH.ASSESSMENT_HISTORY_DETAIL, // /personal-learning-program/:uuid

      // Learning Program pages

      // Authentication pages
      PATH.AUTH_LOGIN, // /login
      PATH.AUTH_REGISTER, // /register
      PATH.AUTH_FORGOT_PASSWORD, // /forgot-password
      PATH.AUTH_RESET_PASSWORD, // /reset-password
      PATH.AUTH_CREATE_NEW_PASSWORD, // /create-new-password
      PATH.AUTH_OTP, // /otp
      PATH.AUTH_REGISTER_SUCCESS, // /register/success

      // Profile pages
      PATH.PROFILE, // /profile
      PATH.PROFILE_UPDATE, // /profile/update
      PATH.PROFILE_CHANGE_PASSWORD, // /profile/change-password

      // Payment pages
      PATH.PAYMENT, // /payment
      PATH.PAYMENT_BULK, // /payment/bulk
      PATH.PAYMENT_ASSESSMENT, // /payment/assessment

      // Transaction pages
      PATH.TRANSACTION, // /transaction
      PATH.TRANSACTION_CANCELLED, // /transaction/cancelled
      PATH.GUEST_TRANSACTION_STATUS, // /guest/transaction

      // Cart page
      PATH.CART, // /cart

      PATH.TICKET, // /ticket

    ];

    // Check if current path is in marketing-focused paths
    const isMarketingFocusedPath = marketingFocusedPaths.includes(currentPath);

    // Check if current path matches excluded patterns (with params)
    const isExcludedPath = excludedPaths.some(path => {
      if (path.includes(':')) {
        // Handle dynamic paths like /learning-program/:slug
        const pathPattern = path.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${pathPattern}$`);
        return regex.test(currentPath);
      }
      return currentPath === path;
    });

    // Also check for dynamic course/event detail pages that should be excluded
    const isDynamicDetailPage =
      currentPath.match(/^\/course\/[^/]+$/) ||
      currentPath.match(/^\/event\/[^/]+$/) ||
      currentPath.match(/^\/article\/[^/]+$/) ||
      currentPath.match(/^\/ticket\/[^/]+$/) ||
      currentPath.match(/^\/payment\/[^/]+\/status$/) ||
      currentPath.match(/^\/transaction\/[^/]+$/);

    const shouldShow = isMarketingFocusedPath && !isExcludedPath && !isDynamicDetailPage;

    return shouldShow;
  };

  const renderContent = () => {
    const getMainTopSpacing = () => {
      if (!isNewLandingPage && showHeader) {
        // if (isBannerVisible) {
        //   return `${bannerHeight + navbarHeight}px`;
        // }
        return `${navbarHeight}px`;
      }
      return '0px';
    };

    return (
      <>
        {isNewLandingPage ? (
          <NewNavbar />
        ) : (
          showHeader && <NewNavbar enableDynamicColors={false} enableBackgroundTransition={false} />
        )}
        <main 
          style={{ 
            marginTop: getMainTopSpacing(),
            transition: 'margin-top 0.3s ease-in-out'
          }}
        >
          {isOnline ? <Routes /> : <OfflineContent />}
        </main>
        {isLoggedIn && showBottomNav && <BottomNav />}
        {showFooter && <Footer />}
      </>
    );
  };

  return (
    <MobileMenuProvider>
      <Head />
      <Notification />
      <TopLoader
        show={showTopLoadingBar || !isInitialLoadComplete || isClientLoading}
        backgroundColor="#ddd"
        thickness={3}
        zIndex={100}
        delay={0}
        color={COLOR.PRIMARY}
        duration={1000}
      />
      {renderContent()}
      {/* {isInitialLoadComplete && !isBannerLoading && banners && shouldShowStickyBanner() && (
        <APIShimmerBanner bannerData={banners} />
      )} */}
    </MobileMenuProvider>
  );
};

export default App;
